import { useQuery } from "@tanstack/react-query";
import { servicesApi, Service } from "@/api/client";

interface ServiceSelectProps {
  value: string;
  onChange: (value: string, service?: Service) => void;
  className?: string;
  placeholder?: string;
  valueKey?: "id" | "slug";
  required?: boolean;
}

export default function ServiceSelect({
  value,
  onChange,
  className = "",
  placeholder = "Select a service",
  valueKey = "slug",
  required = false,
}: ServiceSelectProps) {
  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const services = servicesData?.services || [];

  // Group services by their group
  const grouped = services.reduce(
    (acc, svc) => {
      const groupName = svc.group_name || "Ungrouped";
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(svc);
      return acc;
    },
    {} as Record<string, Service[]>,
  );

  // Sort group names, putting "Ungrouped" last
  const sortedGroupNames = Object.keys(grouped).sort((a, b) => {
    if (a === "Ungrouped") return 1;
    if (b === "Ungrouped") return -1;
    return a.localeCompare(b);
  });

  const getOptionValue = (svc: Service) =>
    valueKey === "slug" ? svc.slug : svc.id;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    const selectedService = services.find(
      (svc) => getOptionValue(svc) === newValue
    );
    onChange(newValue, selectedService);
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      className={`border rounded-lg px-3 py-2 ${className}`}
      required={required}
    >
      <option value="">{placeholder}</option>
      {sortedGroupNames.map((groupName) => (
        <optgroup key={groupName} label={groupName}>
          {grouped[groupName]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((svc) => (
              <option key={svc.id} value={getOptionValue(svc)}>
                {svc.name}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}
